import { Badge } from '@/components/ui/badge'
import { Calendar, Heart, MapPin, Tag } from 'lucide-react'
import { formatDressCode, formatTier, formatTime } from '@/lib/format'
import { resolveBackgroundColor } from '@/lib/eventPalette'
import type { Event } from '@/types'

type EventPreviewProps = {
  event: Event
  organizationName: string
  coverImagePreview?: string | null
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function formatPrice(price: string | null): string {
  if (!price) return 'Price TBD'
  const num = parseFloat(price)
  if (num === 0) return 'Free'
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

function CausesBadges({ causes }: { causes: string[] }) {
  if (!causes.length) return null
  return (
    <div>
      <h3 className="mb-2 text-base font-semibold">Causes</h3>
      <div className="flex flex-wrap gap-1.5">
        {causes.map((cause, index) => (
          <Badge key={cause} variant={index === 0 ? 'default' : 'secondary'} className="text-sm">
            {cause}
          </Badge>
        ))}
      </div>
    </div>
  )
}

function HostsBlock({ hosts }: { hosts: Event['hosts'] }) {
  if (!hosts.length) return null
  return (
    <div>
      <h3 className="mb-2 text-base font-semibold">Hosted by</h3>
      <div className="space-y-2">
        {hosts.map((host, i) => (
          <div key={i} className="border-l-2 border-primary/30 pl-3">
            <p className="font-serif text-base font-semibold">{host.name}</p>
            {host.organization && <p className="text-sm text-muted-foreground">{host.organization}</p>}
          </div>
        ))}
      </div>
    </div>
  )
}

function HonoreesBlock({ honorees }: { honorees: Event['honorees'] }) {
  if (!honorees.length) return null
  return (
    <div>
      <h3 className="mb-2 text-base font-semibold">Honorees</h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {honorees.map((h, i) => (
          <div key={i} className="rounded-lg bg-muted/50 p-3">
            <p className="text-base font-semibold">{h.name}</p>
            {h.company && <p className="text-sm text-muted-foreground">{h.company}</p>}
            {h.award && <p className="mt-1 text-sm">{h.award}</p>}
          </div>
        ))}
      </div>
    </div>
  )
}

function EntertainmentBlock({ entertainment }: { entertainment: Event['entertainment'] }) {
  if (!entertainment.length) return null
  return (
    <div>
      <h3 className="mb-2 text-base font-semibold">Entertainment</h3>
      <div className="space-y-1">
        {entertainment.map((ent, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-base font-medium">{ent.name}</span>
            <Badge variant="secondary" className="text-sm">
              {ent.type.charAt(0).toUpperCase() + ent.type.slice(1)}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  )
}

function SponsorsBlock({ sponsors }: { sponsors: Event['sponsors'] }) {
  if (!sponsors.length) return null
  return (
    <div>
      <h3 className="mb-2 text-base font-semibold">Sponsors</h3>
      <div className="space-y-2">
        {sponsors.map((s, i) => {
          const tierLabel = formatTier(s.tier)
          if (s.featured) {
            return (
              <div key={`${s.name}-${i}`} className="flex items-center gap-2 rounded-lg bg-muted/50 p-2 ring-1 ring-primary/10">
                {s.logo_url && <img src={s.logo_url} alt={s.name} className="size-8 shrink-0 rounded object-contain" />}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{s.name}</p>
                  {tierLabel && <p className="text-xs text-muted-foreground">{tierLabel} Sponsor</p>}
                </div>
              </div>
            )
          }
          return (
            <div key={`${s.name}-${i}`} className="flex items-center gap-1.5">
              {s.logo_url && <img src={s.logo_url} alt={s.name} className="size-5 shrink-0 rounded object-contain" />}
              <span className="text-sm">{s.name}</span>
              {tierLabel && <span className="text-xs text-muted-foreground">{tierLabel}</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CommitteeBlock({ members }: { members: Event['committee_members'] }) {
  if (!members.length) return null
  return (
    <div>
      <h3 className="mb-2 text-base font-semibold">Gala Committee</h3>
      <div className="space-y-0.5">
        {members.map((m, i) => (
          <p key={i} className="text-sm">
            <span className="font-medium">{m.name}</span>
            {m.role && <span className="text-muted-foreground"> — {m.role}</span>}
          </p>
        ))}
      </div>
    </div>
  )
}

function HashtagsBlock({ hashtags }: { hashtags: string[] }) {
  if (!hashtags.length) return null
  return (
    <div className="flex flex-wrap gap-1.5">
      {hashtags.map((tag) => (
        <Badge key={tag} variant="secondary" className="text-sm">{tag}</Badge>
      ))}
    </div>
  )
}

function CountdownPlaceholder() {
  return (
    <div className="flex justify-center gap-4 rounded-lg bg-primary/5 p-3 text-center">
      {['days', 'hrs', 'min'].map((label) => (
        <div key={label}>
          <p className="text-xl font-semibold tabular-nums">--</p>
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
        </div>
      ))}
    </div>
  )
}

function PricePlaceholder({ event }: { event: Event }) {
  const ctaLabel = event.purchase_button_text?.trim() || 'Purchase Tickets'

  return (
    <div className="rounded-lg border border-black/5 bg-card p-3">
      <p className="text-sm font-medium text-muted-foreground">Starting at</p>
      <p className="text-xl font-semibold tabular-nums">{formatPrice(event.starting_ticket_price)}</p>
      {(event.auction_items || event.gift_items) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {event.auction_items && <Badge variant="secondary" className="text-sm">Auction Items</Badge>}
          {event.gift_items && <Badge variant="secondary" className="text-sm">Gift Items</Badge>}
        </div>
      )}
      <div className="mt-2 rounded-md bg-primary py-1.5 text-center text-sm font-medium text-primary-foreground">
        {ctaLabel}
      </div>
    </div>
  )
}

function DonationPlaceholder({ event }: { event: Event }) {
  return (
    <div className="rounded-lg border border-primary/15 bg-primary/5 p-3">
      <div className="mb-2 flex items-center gap-2">
        <Heart className="size-4 text-primary" fill="currentColor" />
        <p className="text-base font-semibold">Donate</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[2500, 5000, 10_000, 25_000].map((cents) => (
          <div key={cents} className="rounded-md border border-black/10 bg-background px-2 py-1.5 text-center text-sm font-semibold tabular-nums">
            ${(cents / 100).toLocaleString()}
          </div>
        ))}
      </div>
      <div className="mt-2 rounded-md bg-primary py-1.5 text-center text-sm font-medium text-primary-foreground">
        {event.donations_enabled ? 'Donate' : 'Donation link'}
      </div>
    </div>
  )
}

function DetailRow({ icon, primary, secondary }: { icon: React.ReactNode; primary: string; secondary?: string | null }) {
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5 rounded-md bg-primary/10 p-1.5">{icon}</div>
      <div>
        <p className="text-base font-semibold">{primary}</p>
        {secondary && <p className="text-sm text-muted-foreground">{secondary}</p>}
      </div>
    </div>
  )
}

function MapPlaceholder() {
  return (
    <div className="flex h-32 items-center justify-center rounded-lg bg-muted/40 text-sm text-muted-foreground">
      Map
    </div>
  )
}

export default function EventPreview({ event, organizationName, coverImagePreview }: EventPreviewProps) {
  const dressCode = formatDressCode(event.dress_code)
  const startTime = formatTime(event.start_time)
  const endTime = formatTime(event.end_time)
  const causeBadges = [event.primary_cause, ...event.causes]
    .filter((cause): cause is string => Boolean(cause))
    .filter((cause, index, allCauses) => allCauses.indexOf(cause) === index)
  const timeRange = startTime && endTime ? `${startTime} – ${endTime}` : startTime || endTime || null

  const hasImage = !!coverImagePreview
  const title = event.title || 'Untitled Event'
  const dateDisplay = event.date ? formatDate(event.date) : 'Date TBD'

  const locationLines = [event.venue_name, event.street_address, [event.city, event.state].filter(Boolean).join(', ')]
    .filter((line): line is string => Boolean(line))

  const backgroundColor = resolveBackgroundColor(event.background_color)
  const isGallery = event.template_style === 'gallery'
  const isFundraiser = event.page_kind === 'fundraiser'
  const templateLabel = isFundraiser
    ? (isGallery ? 'Gallery fundraiser' : 'Classic fundraiser')
    : isGallery ? 'Gallery template' : 'Classic template'

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{templateLabel}</span>
        <span className="inline-flex items-center gap-2">
          Background
          <span className="inline-block size-4 rounded-full border border-black/10" style={{ backgroundColor }} />
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl ring-1 ring-black/10" style={{ backgroundColor }}>
        {isFundraiser && isGallery ? (
          <div className="space-y-6 p-5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:items-stretch">
              {hasImage ? (
                <div className="aspect-[4/5] overflow-hidden rounded-xl sm:aspect-auto sm:h-full">
                  <img src={coverImagePreview!} alt="" className="h-full w-full object-cover" />
                </div>
              ) : (
                <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-linear-to-br from-[oklch(0.45_0.20_260)] via-primary to-[oklch(0.50_0.18_240)] sm:aspect-auto sm:h-full">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,255,255,0.12),transparent)]" />
                </div>
              )}
              <div className="flex flex-col gap-4">
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight text-balance">{title}</h2>
                  <p className="mt-1 text-base text-muted-foreground">{organizationName}</p>
                </div>
                <DonationPlaceholder event={event} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:items-start">
              <div className="space-y-5">
                {event.description && (
                  <div>
                    <h3 className="mb-2 text-base font-semibold">About this fundraiser</h3>
                    <p className="text-sm whitespace-pre-wrap text-pretty">{event.description}</p>
                  </div>
                )}
              </div>
              <div className="space-y-5">
                <CausesBadges causes={causeBadges} />
              </div>
            </div>
          </div>
        ) : isFundraiser ? (
          <div className="space-y-6 p-5">
            {hasImage ? (
              <div className="relative h-44 overflow-hidden rounded-xl">
                <img src={coverImagePreview!} alt="" className="h-full w-full object-cover" />
              </div>
            ) : (
              <div className="relative h-44 overflow-hidden rounded-xl bg-linear-to-br from-[oklch(0.45_0.20_260)] via-primary to-[oklch(0.50_0.18_240)]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,255,255,0.12),transparent)]" />
              </div>
            )}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div className="space-y-4 sm:col-span-2">
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight text-balance">{title}</h2>
                  <p className="mt-1 text-base text-muted-foreground">{organizationName}</p>
                </div>
                {event.description && (
                  <div>
                    <h3 className="mb-2 text-base font-semibold">About this fundraiser</h3>
                    <p className="text-sm whitespace-pre-wrap text-pretty">{event.description}</p>
                  </div>
                )}
                <CausesBadges causes={causeBadges} />
              </div>
              <DonationPlaceholder event={event} />
            </div>
          </div>
        ) : isGallery ? (
          <div className="space-y-6 p-5">
            {/* Top row: banner left, details right */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:items-stretch">
              {hasImage ? (
                <div className="aspect-[4/5] overflow-hidden rounded-xl sm:aspect-auto sm:h-full">
                  <img src={coverImagePreview!} alt="" className="h-full w-full object-cover" />
                </div>
              ) : (
                <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-linear-to-br from-[oklch(0.45_0.20_260)] via-primary to-[oklch(0.50_0.18_240)] sm:aspect-auto sm:h-full">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,255,255,0.12),transparent)]" />
                </div>
              )}
              <div className="flex flex-col gap-4">
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight text-balance">{title}</h2>
                  <p className="mt-1 text-base text-muted-foreground">{organizationName}</p>
                </div>
                <div className="space-y-2">
                  <DetailRow icon={<Calendar className="size-3.5 text-primary" />} primary={dateDisplay} secondary={timeRange} />
                  {locationLines.length > 0 && (
                    <DetailRow
                      icon={<MapPin className="size-3.5 text-primary" />}
                      primary={locationLines[0]}
                      secondary={locationLines.slice(1).join(', ') || undefined}
                    />
                  )}
                  {dressCode && (
                    <DetailRow icon={<Tag className="size-3.5 text-primary" />} primary={dressCode} secondary="Dress Code" />
                  )}
                </div>
                <PricePlaceholder event={event} />
              </div>
            </div>

            {/* Sections + about — columns flow independently */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:items-start">
              <div className="space-y-5">
                <SponsorsBlock sponsors={event.sponsors} />
                <HonoreesBlock honorees={event.honorees} />
                <EntertainmentBlock entertainment={event.entertainment} />
                <CommitteeBlock members={event.committee_members} />
                <HostsBlock hosts={event.hosts} />
                {event.countdown_timer && (
                  <div>
                    <h3 className="mb-2 text-base font-semibold">Countdown</h3>
                    <CountdownPlaceholder />
                  </div>
                )}
              </div>
              <div className="space-y-5">
                {event.description && (
                  <div>
                    <h3 className="mb-2 text-base font-semibold">About the Event</h3>
                    <p className="text-sm whitespace-pre-wrap text-pretty">{event.description}</p>
                  </div>
                )}
                <CausesBadges causes={causeBadges} />
                <HashtagsBlock hashtags={event.hashtags} />
                {locationLines.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-base font-semibold">Venue</h3>
                    <MapPlaceholder />
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            {hasImage ? (
              <div className="relative h-44 overflow-hidden">
                <img src={coverImagePreview!} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 px-5 pb-4">
                  <h2 className="text-xl font-semibold tracking-tight text-white text-balance">{title}</h2>
                  <p className="mt-0.5 text-base text-white/80">Hosted by {organizationName}</p>
                </div>
              </div>
            ) : (
              <div className="relative overflow-hidden bg-linear-to-br from-[oklch(0.45_0.20_260)] via-primary to-[oklch(0.50_0.18_240)]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,255,255,0.12),transparent)]" />
                <div className="relative px-5 py-6">
                  <h2 className="text-xl font-semibold tracking-tight text-white text-balance">{title}</h2>
                  <p className="mt-0.5 text-base text-white/80">Hosted by {organizationName}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-white/70">
                    <span className="flex items-center gap-1"><Calendar className="size-3.5" />{dateDisplay}</span>
                    {event.venue_name && (
                      <span className="flex items-center gap-1"><MapPin className="size-3.5" />{event.venue_name}</span>
                    )}
                    {dressCode && (
                      <Badge className="bg-white/15 text-white hover:bg-white/15 text-sm">{dressCode}</Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="p-5">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                <div className="space-y-6 lg:col-span-3">
                  {event.description && (
                    <p className="text-base whitespace-pre-wrap text-pretty">{event.description}</p>
                  )}
                  <CausesBadges causes={causeBadges} />
                  <HostsBlock hosts={event.hosts} />
                  <HonoreesBlock honorees={event.honorees} />
                  <EntertainmentBlock entertainment={event.entertainment} />
                  <SponsorsBlock sponsors={event.sponsors} />
                  <CommitteeBlock members={event.committee_members} />
                  <HashtagsBlock hashtags={event.hashtags} />
                </div>

                <div className="lg:col-span-2">
                  <div className="space-y-4 rounded-xl bg-muted/30 p-4 ring-1 ring-black/5">
                    {event.countdown_timer && <CountdownPlaceholder />}
                    <DetailRow icon={<Calendar className="size-3.5 text-primary" />} primary={dateDisplay} secondary={timeRange} />
                    {locationLines.length > 0 && (
                      <DetailRow
                        icon={<MapPin className="size-3.5 text-primary" />}
                        primary={locationLines[0]}
                        secondary={locationLines.slice(1).join(', ') || undefined}
                      />
                    )}
                    {dressCode && (
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5 rounded-md bg-primary/10 p-1.5">
                          <Tag className="size-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Dress Code</p>
                          <p className="text-base font-semibold">{dressCode}</p>
                        </div>
                      </div>
                    )}
                    <PricePlaceholder event={event} />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
